import type { MarketIdentifiers } from '$lib/types/market';
import {
	cancelFullOrderRequest,
	cancelPairOrderRequest,
	submitFullOrderRequest,
	submitPairOrderRequest
} from '../connectors/advancedOrders';
import BigNumber from 'bignumber.js';
import { LARGE_NEGATIVE_INT, LARGE_POSITIVE_INT } from '../contants';
import { getPairsFromTpsl } from '../utils/tpsl';

export const buildPairOrderCallData = (
	marketIdentifier: MarketIdentifiers,
	sizeDelta: string,
	isEntirePosition: boolean,
	priceImpact: string,
	expiry: string,
	takeProfitInput: string,
	stopLossInput: string
) => {
	const isTakeProfit = new BigNumber(takeProfitInput).gt(0);
	const isStopLoss = new BigNumber(stopLossInput).gt(0);

	if (!isTakeProfit && !isStopLoss) return { _targetArray: [], _callData: [] };

	const isLong = new BigNumber(sizeDelta).isPositive();
	const { firstPairA, firstPairB, secondPairA, secondPairB } = getPairsFromTpsl(
		takeProfitInput,
		stopLossInput,
		isLong
	);

	const pairOrderSizeDelta = isEntirePosition
		? isLong
			? LARGE_NEGATIVE_INT
			: LARGE_POSITIVE_INT
		: new BigNumber(sizeDelta).negated().toFixed();

	const { _target, _callData } = submitPairOrderRequest(
		marketIdentifier,
		pairOrderSizeDelta,
		priceImpact,
		expiry,
		firstPairA,
		firstPairB,
		secondPairA,
		secondPairB
	);

	return { _targetArray: [_target], _callData: [_callData] };
};

export const buildFullOrderCallData = (
	marketIdentifier: MarketIdentifiers,
	sizeDelta: string,
	priceImpact: string,
	expiry: string,
	limitPrice: string,
	triggerPrice: string,
	takeProfitInput: string,
	stopLossInput: string
) => {
	const isLong = new BigNumber(sizeDelta).isPositive();

	let priceA = triggerPrice;
	let priceB = new BigNumber(limitPrice).gt(0) ? limitPrice : '1';

	if (new BigNumber(priceA).gt(new BigNumber(priceB))) {
		[priceA, priceB] = [priceB, priceA];
	}

	const { firstPairA, firstPairB, secondPairA, secondPairB } = getPairsFromTpsl(
		takeProfitInput,
		stopLossInput,
		isLong
	);

	const { _target, _callData } = submitFullOrderRequest(
		marketIdentifier,
		sizeDelta,
		priceImpact,
		expiry,
		priceA,
		priceB,
		firstPairA,
		firstPairB,
		secondPairA,
		secondPairB
	);

	return { _targetArray: [_target], _callData: [_callData] };
};

export const buildCancelPairOrderCallData = async (orderId: string) => {
	const targetArray: Array<string> = [];
	const callDataArray: Array<string> = [];

	const cancelTradeSmartContract = cancelPairOrderRequest(orderId);

	targetArray.push(cancelTradeSmartContract._target);
	callDataArray.push(cancelTradeSmartContract._callData);

	return {
		_targetArray: targetArray,
		_callDataArray: callDataArray
	};
};

export const buildCancelFullOrderCallData = async (orderId: string) => {
	const targetArray: Array<string> = [];
	const callDataArray: Array<string> = [];

	const cancelTradeSmartContract = cancelFullOrderRequest(orderId);

	targetArray.push(cancelTradeSmartContract._target);
	callDataArray.push(cancelTradeSmartContract._callData);

	return {
		_targetArray: targetArray,
		_callDataArray: callDataArray
	};
};

export const buildCancelAllOrdersCallData = async (orderIds: Array<string>) => {
	const targetArray: Array<string> = [];
	const callDataArray: Array<string> = [];

	orderIds.forEach((orderId) => {
		const cancelTradeSmartContract = cancelFullOrderRequest(orderId);

		targetArray.push(cancelTradeSmartContract._target);
		callDataArray.push(cancelTradeSmartContract._callData);
	});

	return {
		_targetArray: targetArray,
		_callDataArray: callDataArray
	};
};
